import React from 'react';
import { withRouter } from 'react-router';
import Moment from 'moment';
import braintreeDropIn from 'braintree-web-drop-in';
import queryString from 'query-string';

import { SERVER_ERROR_SHORT } from '../../constants/messages';
import CONSTANTS from '../../constants/constants';
import AuthMixin from '../../mixins/authenticationMixin';
import authAPI from '../../api/authApi';
import AppAuthStore from '../../stores/login-store';
import AppRoutes from '../../routes/routes';
import upgradePlans from '../../data/upgrade-plans';
import Analytics from '../../services/analytics';
import { DismissButton, ModalAlert, ModalHeader, ModalProgress, ModalStatusIcon } from '../popups/modal-parts';

class PaymentModal extends React.Component {
    constructor() {
        super();
        this.state = {
            title: 'Checkout',
            displayCheckout: false,
            billingDate: '',
            nextBillingDate: '',
            errorMessage: false,
            loading: true,
            success: false,
            error: false,
            processing: false,
        };

        this._dropin = null;
        this._dropinCallback = this._dropinCallback.bind(this);
        this._onUpgradeClick = this._onUpgradeClick.bind(this);
        this._submit = this._submit.bind(this);
    }

    _dropinCallback(createErr, instance) {
        if (createErr) {
            // Handle any errors that might've occurred when creating Drop-in
            if ('Rollbar' in window) {
                Rollbar.error('Dropin could not be created', createErr);
            }
            return;
        }

        this._dropin = instance;
        this.setState({ loading: false, errorMessage: false });
    }

    // Lets the user pick a card again after a failed attempt; a nonce can only be used once.
    _resetPaymentMethod(errorMessage) {
        if (this._dropin) {
            this._dropin.clearSelectedPaymentMethod();
        }
        this.setState({ processing: false, loading: false, errorMessage });
    }

    _onUpgradeClick(e) {
        e.preventDefault();

        // Guard against double clicks while 3DS or the server call is in flight;
        // each click would otherwise produce its own subscription attempt.
        if (!this._dropin || this.state.processing) {
            return;
        }

        this.setState({ processing: true, errorMessage: false });

        this._dropin.requestPaymentMethod(
            {
                threeDSecure: {
                    amount: Number(this.props.amount).toFixed(2),
                    email: this.props.user.email,
                    // Always challenge the first payment: it establishes SCA for the
                    // subscription so issuers are less likely to decline renewals.
                    challengeRequested: true,
                },
            },
            (requestPaymentMethodErr, payload) => {
                if (requestPaymentMethodErr) {
                    // Includes the user closing the 3DS challenge, so let them try again
                    console.error(requestPaymentMethodErr);
                    this._resetPaymentMethod('We could not verify your payment details. Please try again.');
                    return;
                }

                const info = payload.threeDSecureInfo;

                // Only stop when the issuer actually failed the authentication. Where 3DS
                // wasn't possible (unsupported card, lookup errors, etc.) let Braintree's
                // risk and 3DS rules decide instead of rejecting here.
                if (payload.type === 'CreditCard' && info && info.liabilityShiftPossible && !info.liabilityShifted) {
                    this._resetPaymentMethod(
                        'Your bank was unable to verify this card. Please try again or use a different payment method.'
                    );
                    return;
                }

                this._submit(payload.nonce);
            }
        );
    }

    componentWillUnmount() {
        if (this._dropin) {
            this._dropin.teardown(() => {});
            this._dropin = null;
        }
    }

    componentDidMount() {
        Analytics.event('begin_checkout', this.ecommerceParams());

        authAPI.getPaymentToken(this.props.userApiRequest, {
            onSuccess: ({ data }) => {
                const clientToken = data.client_token;

                braintreeDropIn.create(
                    {
                        authorization: clientToken,
                        threeDSecure: true,
                        container: '#bt-dropin',
                        paypal: {
                            flow: 'vault',
                        },
                    },
                    this._dropinCallback
                );
            },
            // without a client token the drop-in never loads, so don't leave the
            // spinner up forever
            onError: () => {
                this.setState({
                    title: SERVER_ERROR_SHORT,
                    errorMessage: "We couldn't load the secure checkout.",
                    error: true,
                    loading: false,
                });
            },
        });
    }

    componentDidCatch(e) {
        if ('Rollbar' in window) {
            Rollbar.error('Unable to process payment', e);
        }
    }

    // GA4 ecommerce parameters for the plan being bought, at the price actually charged
    ecommerceParams() {
        const plan = this.selectedPlan() || {};
        const value = Number(this.props.amount) || 0;
        const promo = queryString.parse(this.props.location.search).promo;
        return {
            currency: 'GBP',
            value,
            ...(promo ? { coupon: promo } : {}),
            items: [{ item_id: plan.plan_id, item_name: plan.type, price: value, quantity: 1 }]
        };
    }

    selectedPlan() {
        return upgradePlans.filter(item =>
            Boolean(item.plan_id === queryString.parse(this.props.location.search).plan_id)
        )[0];
    }

    _submit(nonce) {
        this.setState({ loading: true });

        let data = {
            payment_method_nonce: nonce,
            plan_id: queryString.parse(this.props.location.search).plan_id,
            promo: queryString.parse(this.props.location.search).promo,
        };

        authAPI.sendPaymentDetails(this.props.userApiRequest, data, {
            onSuccess: ({ data }) => {
                const user = AppAuthStore.getSessionUserData();
                Analytics.event('purchase', { ...this.ecommerceParams(), transaction_id: `${user.id}-${Date.now()}` });
                AppAuthStore.updateUser(this.props.userApiRequest, { account_type: data.account_type });

                this.setState({
                    title: 'Your account has been upgraded!',
                    billingDate: data.billing_date,
                    nextBillingDate: data.next_billing_date,
                    loading: false,
                    processing: false,
                    success: true,
                });
            },
            onError: e => {
                if ('Rollbar' in window) {
                    Rollbar.error('Payment error occurred', e);
                }

                const status = e.response && e.response.statusCode;
                const serverMessage = e.error && e.error.errors && e.error.errors[0];

                // A declined payment means nothing was created, so the user can safely retry
                if (status === 422 && serverMessage) {
                    this._resetPaymentMethod(serverMessage);
                    return;
                }

                // Anything else (timeout, gateway error, already processing) may have
                // succeeded on the server, so don't invite an immediate retry.
                this.setState({
                    title: SERVER_ERROR_SHORT,
                    errorMessage: serverMessage || false,
                    error: true,
                    success: false,
                    loading: false,
                    processing: false,
                });
            },
        });
    }

    renderError() {
        return (
            <div className="modal-body is-centered">
                <ModalStatusIcon type="error" />
                <ModalHeader title={this.state.title} />
                <div className="modal-text">
                    {this.state.errorMessage ? <p><strong>{this.state.errorMessage}</strong></p> : null}
                    <p>
                        Please refresh the page to check whether your account has been upgraded before trying
                        again. If you need help, contact us at{' '}
                        <a href={`mailto:${CONSTANTS.CONTACT_EMAIL}`}>{CONSTANTS.CONTACT_EMAIL}</a>
                    </p>
                </div>
                <div className="modal-actions">
                    <DismissButton className="modal-btn modal-btn-secondary">Close</DismissButton>
                </div>
            </div>
        );
    }

    renderSuccess() {
        const plan = this.selectedPlan();
        const startDate = Moment(this.state.billingDate).format('D MMMM YYYY');
        const endDate = Moment(this.state.nextBillingDate).format('D MMMM YYYY');

        return (
            <div className="modal-body is-centered">
                <ModalStatusIcon type="success" />
                <ModalHeader title={this.state.title} />
                <div className="modal-text">
                    <p>
                        You're now on the <strong>{plan.type}</strong> plan with unlimited access to every
                        checklist.
                    </p>
                    <p>
                        Your subscription started on {startDate}.
                        {plan.recurrence
                            ? ` Your next billing date will be on or after ${endDate}.`
                            : ` Your access will last until ${endDate} and you will not be charged again.`}
                    </p>
                </div>
                <div className="modal-actions">
                    <DismissButton>Start exploring</DismissButton>
                </div>
            </div>
        );
    }

    renderForm() {
        const plan = this.selectedPlan();
        const { errorMessage, loading, processing } = this.state;

        return (
            <div className="modal-body">
                <ModalHeader title={this.state.title} sub="Complete your upgrade with a card or PayPal." />

                <div className="modal-plan">
                    <div>
                        <div className="modal-plan-name">{plan.type}</div>
                        <div className="modal-plan-desc">
                            {plan.recurrence ? `Renews every ${plan.recurrence}` : 'One-off payment, no renewal'}
                        </div>
                    </div>
                    <div className="modal-plan-price">
                        &pound;{this.props.amount}
                        <small className="is-block">due today</small>
                    </div>
                </div>

                <form
                    id="paypal-container"
                    className="modal-form"
                    method="post"
                    ref="form"
                    action="/checkout"
                    onSubmit={e => e.preventDefault()}
                >
                    <div className="paypal-container" id="bt-dropin"></div>

                    {errorMessage ? <ModalAlert>{errorMessage}</ModalAlert> : null}

                    <button
                        className="modal-btn modal-btn-primary"
                        disabled={loading || processing}
                        onClick={this._onUpgradeClick}
                        type="submit"
                    >
                        {processing ? 'Processing…' : 'Upgrade'}
                    </button>
                </form>

                <p className="modal-footnote">
                    By clicking upgrade you confirm that you have read, understood and accepted the{' '}
                    <a href={AppRoutes.tcs} target="_blank" rel="noopener noreferrer">Terms &amp; Conditions</a>
                </p>

                {loading ? this.renderLoading() : null}
            </div>
        );
    }

    renderLoading() {
        return this.state.processing ? (
            <ModalProgress title="Processing your payment…" text="Please don't close this window." />
        ) : (
            <ModalProgress title="Loading secure checkout…" />
        );
    }

    render() {
        const { error, success } = this.state;

        if (success) {
            return this.renderSuccess();
        }

        if (error) {
            return this.renderError();
        }

        return this.renderForm();
    }
}

export default AuthMixin(withRouter(PaymentModal));
